import React from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // Log to console in production for debugging
    console.error('Error stack:', error?.stack);
    console.error('Component stack:', errorInfo?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
    window.location.href = '/dashboard';
  };

  handleRefresh = () => {
    window.location.reload();
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.toString() || 'Unknown error';
      const componentStack = this.state.errorInfo?.componentStack || '';
      
      return (
        <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
          <div className="max-w-lg w-full text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Something went wrong</h1>
            <p className="text-neutral-400 mb-6">
              We encountered an unexpected error. Please try refreshing the page.
            </p>
            
            {/* Always show error summary */}
            <div className="mb-4 p-3 rounded-lg bg-neutral-900/50 border border-neutral-800">
              <p className="text-red-400 text-sm font-mono break-all">{errorMessage}</p>
            </div>
            
            {/* Toggle for full details */}
            <button 
              onClick={this.toggleDetails}
              className="text-sm text-neutral-500 hover:text-neutral-300 mb-4 flex items-center justify-center gap-1 mx-auto"
            >
              <Bug className="w-3 h-3" />
              {this.state.showDetails ? 'Hide' : 'Show'} technical details
            </button>
            
            {this.state.showDetails && componentStack && (
              <div className="mb-6 p-4 rounded-lg bg-neutral-900 text-left overflow-auto max-h-60 border border-neutral-800">
                <p className="text-neutral-500 text-xs font-mono whitespace-pre-wrap">{componentStack}</p>
              </div>
            )}
            
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={this.handleRefresh}
                className="btn-secondary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Page
              </Button>
              <Button 
                onClick={this.handleGoHome}
                className="btn-primary"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
